import { doc, getDoc, updateDoc, collection, query, where, getDocs, arrayUnion, arrayRemove } from "firebase/firestore";
import { db } from "../lib/firebase";
import { AppUser } from "../types/user";

export interface ModeratorInfo {
  uid: string;
  email: string;
  name?: string;
  addedAt: Date;
}

export const addModerator = async (instructorId: string, moderatorEmail: string): Promise<{ success: boolean; message: string; moderator?: ModeratorInfo }> => {
  try {
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("email", "==", moderatorEmail.toLowerCase().trim()));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return { success: false, message: "ไม่พบผู้ใช้ที่มีอีเมลนี้ในระบบ" };
    }

    const moderatorDoc = querySnapshot.docs[0];
    const moderatorData = moderatorDoc.data() as AppUser;
    const moderatorId = moderatorDoc.id;

    if (moderatorId === instructorId) {
      return { success: false, message: "ไม่สามารถเพิ่มตัวเองเป็น Moderator ได้" };
    }

    const instructorRef = doc(db, "users", instructorId);
    const instructorSnap = await getDoc(instructorRef);

    if (!instructorSnap.exists()) {
      return { success: false, message: "ไม่พบข้อมูลผู้สอน" };
    }

    const instructorData = instructorSnap.data() as AppUser;
    const currentModerators = instructorData.moderators || [];

    if (currentModerators.includes(moderatorId)) {
      return { success: false, message: "ผู้ใช้นี้เป็น Moderator อยู่แล้ว" };
    }

    await updateDoc(instructorRef, {
      moderators: arrayUnion(moderatorId)
    });

    const moderatorRef = doc(db, "users", moderatorId);
    await updateDoc(moderatorRef, {
      role: "moderator",
      instructorId: instructorId
    });

    const moderatorInfo: ModeratorInfo = {
      uid: moderatorId,
      email: moderatorData.email,
      name: moderatorData.name,
      addedAt: new Date()
    };

    return { 
      success: true, 
      message: `เพิ่ม ${moderatorData.name || moderatorData.email} เป็น Moderator สำเร็จ`,
      moderator: moderatorInfo
    };
  } catch (error: any) {
    console.error("Error adding moderator:", error);
    return { success: false, message: `เกิดข้อผิดพลาด: ${error.message}` };
  }
};

export const removeModerator = async (instructorId: string, moderatorId: string): Promise<{ success: boolean; message: string }> => {
  try {
    const instructorRef = doc(db, "users", instructorId);
    await updateDoc(instructorRef, {
      moderators: arrayRemove(moderatorId)
    });

    const moderatorRef = doc(db, "users", moderatorId);
    const moderatorSnap = await getDoc(moderatorRef);
    
    if (moderatorSnap.exists()) {
      await updateDoc(moderatorRef, {
        role: "student",
        instructorId: null
      });
    }

    return { success: true, message: "ลบ Moderator สำเร็จ" };
  } catch (error: any) {
    console.error("Error removing moderator:", error);
    return { success: false, message: `เกิดข้อผิดพลาด: ${error.message}` };
  }
};

export const getModerators = async (instructorId: string): Promise<ModeratorInfo[]> => {
  try {
    const instructorRef = doc(db, "users", instructorId);
    const instructorSnap = await getDoc(instructorRef);

    if (!instructorSnap.exists()) {
      return [];
    }

    const instructorData = instructorSnap.data() as AppUser;
    const moderatorIds = instructorData.moderators || [];

    if (moderatorIds.length === 0) {
      return [];
    }

    const moderators: ModeratorInfo[] = [];

    for (const modId of moderatorIds) {
      const modRef = doc(db, "users", modId);
      const modSnap = await getDoc(modRef);

      if (modSnap.exists()) {
        const modData = modSnap.data() as AppUser;
        moderators.push({
          uid: modId,
          email: modData.email,
          name: modData.name,
          addedAt: new Date()
        });
      }
    }

    return moderators;
  } catch (error) {
    console.error("Error getting moderators:", error);
    return [];
  }
};
